/**
 * Converts an array of objects to a Map using a specified key.
 * @template T - The type of the objects in the array.
 * @template K - The type of the key property.
 * @param {T[]} arr - The array of objects to convert.
 * @param {K} key - The property to use as the key in the Map.
 * @returns {Map<T[K], T>} - A Map with keys from the specified property and values as the original objects.
 */
export function toMap<T, K extends keyof T>(arr: T[], key: K): Map<T[K], T> {
  return new Map(arr.map((obj) => [obj[key], obj]));
}

/**
 * Converts an array of objects into a Record (dictionary).
 * @param arr - The array of objects to convert.
 * @param key - The property to use as the record key.
 * @returns {Record<Extract<T[K], string | number | symbol>, T>} - A Record with keys from the specified property and values as the original objects.
 */
export function toRecord<T, K extends keyof T>(
  arr: T[],
  key: K,
): Record<Extract<T[K], string | number | symbol>, T> {
  // Define the return type clearly for the compiler
  type KeyType = Extract<T[K], string | number | symbol>;

  return arr.reduce(
    (acc, obj) => {
      const recordKey = obj[key] as unknown as KeyType;
      acc[recordKey] = obj;
      return acc;
    },
    {} as Record<KeyType, T>,
  );
}
